import * as zod from 'zod';

/**
 * Shape for the `roboflowCredentials` feathers service.
 *
 * `create` carries `apiKey` — a long-lived Roboflow workspace key minted in the
 * browser via Sign In With Roboflow — plus an optional `workspaceName` for
 * display. The key is write-only and never read back to the client. `find`
 * returns `configured` + `workspaceName` so the UI can show connection status.
 * Each successful `create` overwrites the previously stored key, so switching
 * Roboflow accounts takes effect immediately.
 */
export const RoboflowCredentialsData = zod.object({
  apiKey: zod.string().optional(),
  /**
   * The non-secret id Roboflow assigns the minted key.
   *
   * The only handle for revoking the key when a robot is disconnected: Roboflow
   * addresses keys by id, and never returns a secret again after minting.
   * Absent on rows written before it was stored, which can be forgotten locally
   * but not revoked in the workspace.
   */
  keyId: zod.string().optional(),
  workspaceName: zod.string().optional(),
  /**
   * The Roboflow folder this robot's key is scoped to, if any.
   *
   * The id is what the key was minted against; the name is what any surface
   * shows, and it cannot be resolved from the id later — the user's token is
   * gone once connect finishes, and the robot's key deliberately does not carry
   * `folder:read`.
   *
   * Absent on a workspace-wide key, which is what a workspace without Advanced
   * API Keys can mint and what every key minted before scoping is.
   */
  folderId: zod.string().optional(),
  folderName: zod.string().optional(),
  configured: zod.boolean().optional(),
});

export type RoboflowCredentialsData = zod.infer<typeof RoboflowCredentialsData>;
